import { BASIC_COLOR_SET } from 'constants/chartConstants';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React from 'react';

// Reusable Horizontal Stacked Bar Chart Component
interface HorizontalBarChartProps {
  className?: string;
  data: { category: string; segments: { name: string; value: number; color?: string }[] }[];
  title: string;
  width?: number;
  xAxisLabel: string;
  yAxisLabel: string;
}

const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({
  className,
  data,
  title,
  xAxisLabel,
  yAxisLabel,
  width,
}) => {
  // Extract unique segment names to define series in the chart
  const segmentNames = Array.from(
    new Set(data.flatMap((item) => item.segments.map((segment) => segment.name)))
  );

  // Construct series data for stacked bar chart
  const series = segmentNames.map((segmentName, index) => ({
    name: segmentName,
    data: data.map((item) => {
      const segment = item.segments.find((seg) => seg.name === segmentName);
      return segment ? segment.value : null; // Assign 0 if there's no value for this segment
    }),
    color: BASIC_COLOR_SET[index],
  }));

  const chartOptions = {
    chart: {
      type: 'bar',
      width,
    },
    title: {
      text: title,
    },
    xAxis: {
      categories: data.map((item) => item.category),
      title: {
        text: xAxisLabel,
      },
      labels: {
        useHTML: true,
        style: {
          textAlign: 'left',
          maxWidth: 200,
          width: 150,
          wordBreak: 'break-all',
          textOverflow: 'ellipsis',
          whiteSpace: 'unset',
          overflow: 'hidden',
        },
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: yAxisLabel,
      },
      stackLabels: {
        enabled: true,
        style: {
          fontWeight: 'bold',
          color: 'black',
        },
      },
    },
    series,
    plotOptions: {
      bar: {
        stacking: 'normal',
        dataLabels: {
          enabled: false,
        },
      },
    },
    tooltip: {
      headerFormat: '<b>{point.x}</b><br/>',
      pointFormat:
        '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y}</b><br/>',
    },
    legend: {
      enabled: true,
    },
    credits: {
      enabled: false,
    },
    accessibility: {
      enabled: false,
    },
  };

  return (
    <div className={className}>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </div>
  );
};

export default HorizontalBarChart;
